import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { combineLatest, Observable, of, tap } from 'rxjs';
import { map } from 'lodash';

import {
  EnquiryConfig,
  EnquiryConfigUpdate,
  ProposalQuickRaterQuotePayload,
  ProposalQuickRaterQuoteRaw,
  Quote,
  QuoteAddon,
  QuoteAddonOptions,
  QuoteOption,
  QuoteStatusUpdate,
  QuoteTierOptions,
} from '@lockton-apps/shared/interfaces';
import { LSOS_BASE_PATH } from '@lockton-apps/shared/util/base-path';
import { ClassOfBusinessId } from '@lockton-apps/shared/util/constants';
import { ProposalMocksService } from '@lockton-apps/shared/util/proposal-utils';
import { getDevConfig } from '@lockton-apps/shared/util/helpers';

/**
 * @depractated Please use quotes service 2 for projects following MMPI
 * */
@Injectable({
  providedIn: 'root',
})
export class QuotesService {
  constructor(
    @Inject('BASE_API_URL') private baseUrl: string,
    @Inject('APP_URL') private appBaseUrl: string,
    @Inject(LSOS_BASE_PATH) private lsosBasePath: string,
    private http: HttpClient,
    private proposalMocks: ProposalMocksService
  ) {}

  getQuotes(): Observable<Quote[]> {
    return this.http.get<Quote[]>(`${this.baseUrl}/quote`);
  }

  getQuote(id: number): Observable<Quote> {
    return this.http.get<Quote>(`${this.baseUrl}/quote/${id}`);
  }

  getQuoteByProposalId(
    proposalId: string,
    portalId: number,
    configId: number,
    clientId: number
  ): Observable<Quote> {
    if (getDevConfig('proposalForm.mockData')) {
      return of(
        this.proposalMocks.createMockedQuote(
          proposalId,
          portalId,
          configId,
          clientId
        )
      );
    }

    return this.http.get<Quote>(`${this.baseUrl}/proposal/${proposalId}/quote`);
  }

  getQuotesByClientId(clientId: number): Observable<Quote[]> {
    return this.http.get<Quote[]>(`${this.baseUrl}/client/${clientId}/quote`);
  }

  initialise(config: EnquiryConfig): Observable<Quote[]> {
    return this.http.post<Quote[]>(`${this.baseUrl}/quote`, config);
  }

  reconfigure(config: EnquiryConfigUpdate): Observable<Quote[]> {
    return this.http.patch<Quote[]>(`${this.baseUrl}/quote`, config);
  }

  /***
    @deprecated only used for legacy LSOS
  */
  legacyGetQuotes(): Observable<Quote[]> {
    return this.http.get<Quote[]>(`${this.legacyGetUrl()}/quotes`);
  }

  /*** 
    @deprecated only used for legacy LSOS
  */
  legacyGetQuotesByClientId(clientId: number): Observable<Quote[]> {
    return this.http.get<Quote[]>(`${this.legacyGetUrl()}/quotes/${clientId}`);
  }

  /*** 
    @deprecated only used for legacy LSOS
  */
  legacyGetQuote(quoteId: number): Observable<Quote> {
    return this.http.get<Quote>(`${this.legacyGetUrl()}/quote/${quoteId}`);
  }

  /*** 
    @deprecated only used for legacy LSOS
  */
  legacyApproveQuote(quote: Quote): Observable<Quote> {
    return this.http.patch<Quote>(
      `${this.legacyGetUrl()}/quote/broker-approve`,
      {
        quoteId: quote.id,
        mailMessage: {
          from: {
            name: 'Media Maker',
            email: 'masterpolicyteam@uk.lockton.com',
          },
          templateId: 'd-62d9e5e3f5bd402e9ba83eb1ba558f82',
          templateData: {
            buttonLink: `${this.appBaseUrl}/${this.lsosBasePath}/${quote.clientId}/quotes/${quote.id}`,
          },
        },
      }
    );
  }

  /*** 
    @deprecated only used for legacy LSOS
  */
  legacyRequestChanges(quoteId: number): Observable<Quote> {
    return this.http.patch<Quote>(
      `${this.legacyGetUrl()}/quote/request-change/${quoteId}`,
      null
    );
  }

  /*** 
    @deprecated only used for legacy LSOS
  */
  legacyReQuote(quote: Quote): Observable<Quote> {
    return this.http.patch<Quote>(
      `${this.legacyGetUrl()}/quote/requote/${quote.id}`,
      {
        quoteId: quote.id,
        mailMessage: {
          from: {
            name: 'Media Maker',
            email: 'masterpolicyteam@uk.lockton.com',
          },
          templateId: 'd-62d9e5e3f5bd402e9ba83eb1ba558f82',
          templateData: {
            buttonLink: `${this.appBaseUrl}/${this.lsosBasePath}/${quote.clientId}/quotes/${quote.id}`,
          },
        },
      }
    );
  }

  assignLeadBroker(quoteId: number, userId: string | null) {
    return this.http
      .patch(`${this.baseUrl}/quote/assign`, { quoteId, userId })
      .pipe(tap((res) => console.log(res)));
  }

  release(quoteId: number): Observable<Quote> {
    return this.http.patch<Quote>(
      `${this.baseUrl}/quote/${quoteId}/release`,
      {}
    );
  }

  approve(quoteId: number, comment: string): Observable<Quote> {
    return this.http.patch<Quote>(`${this.baseUrl}/quote/approve`, {
      quoteId,
      comment,
    });
  }

  updateStatus(update: QuoteStatusUpdate): Observable<Quote> {
    return this.http.patch<Quote>(`${this.baseUrl}/quote/status`, update);
  }

  updateAddon(quoteId: number, payload: unknown): Observable<QuoteAddon> {
    return this.http.put<QuoteAddon>(
      `${this.baseUrl}/quote/${quoteId}/addon`,
      payload
    );
  }

  updateOption(payload: QuoteOption): Observable<QuoteOption> {
    return this.http.put<QuoteOption>(`${this.baseUrl}/quote/option`, payload);
  }

  complete(quoteIds: number[]): Observable<Quote[]> {
    const apiCalls = map(quoteIds, (quoteId) =>
      this.http.patch<Quote>(`${this.baseUrl}/quote/${quoteId}/complete`, {})
    );

    return combineLatest(apiCalls);
  }

  getOptionsForClassOfBusiness(
    quoteId: number,
    cob: ClassOfBusinessId
  ): Observable<QuoteTierOptions[]> {
    return this.http.get<QuoteTierOptions[]>(
      `${this.baseUrl}/quote/${quoteId}/class-of-business/${cob}/option`
    );
  }

  getOptions(
    quoteId: number
  ): Observable<Record<ClassOfBusinessId, QuoteAddonOptions>> {
    return this.http.get<Record<ClassOfBusinessId, QuoteAddonOptions>>(
      `${this.baseUrl}/quote/${quoteId}/option`
    );
  }

  getQuickQuote(
    payload: ProposalQuickRaterQuotePayload
  ): Observable<ProposalQuickRaterQuoteRaw> {
    return this.http.put<ProposalQuickRaterQuoteRaw>(
      `${this.baseUrl}/quote/quick-quote`,
      payload
    );
  }

  updateChecklist(
    quoteId: number,
    checklist: Record<string, boolean>
  ): Observable<Quote> {
    return this.http.put<Quote>(
      `${this.baseUrl}/quote/${quoteId}/checklist`,
      checklist
    );
  }

  private legacyGetUrl(): string {
    return `${this.baseUrl}/partners/lsos`;
  }
}
